export const ADMIN_USERS_CACHE_KEY = 'admin-users-cache-v1';
export const ADMIN_BRANCHES_CACHE_KEY = 'admin-branches-cache-v1';
export const CACHE_MAX_AGE_MS = 5 * 60 * 1000;
export const DOC_FOLDER_ORDER = ['Legal info', 'Handleidingen', 'Functioneel Beheer', 'Development docs'];

export function readCachedAdminCollection(cacheKey, maxAgeMs = CACHE_MAX_AGE_MS) {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(cacheKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.data)) return null;
    if (Number.isFinite(parsed.savedAt) && (Date.now() - parsed.savedAt) > maxAgeMs) {
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn('[AdministratiePanel] Failed to read cache', cacheKey, error);
    return null;
  }
}

export function writeCachedAdminCollection(cacheKey, data) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(cacheKey, JSON.stringify({ data, savedAt: Date.now() }));
  } catch (error) {
    console.warn('[AdministratiePanel] Failed to write cache', cacheKey, error);
  }
}

export function resolveDocumentationFolder(fileName = '', title = '') {
  const value = `${fileName} ${title}`.toLowerCase();

  if (
    value.includes('privacy')
    || value.includes('security')
    || value.includes('datalek')
    || value.includes('dataretentie')
    || value.includes('incident')
    || value.includes('dpa')
    || value.includes('verwerkers')
    || value.includes('legal')
    || value.includes('rollen')
    || value.includes('user-roles')
    || value.includes('dpia')
    || value.includes('or_sociaaloverleg')
  ) {
    return 'Legal info';
  }

  if (
    value.includes('handleiding')
    || value.includes('robotbeheer')
    || value.includes('robotdossiers')
    || value.includes('barcode')
    || value.includes('screen')
  ) {
    return 'Handleidingen';
  }

  if (
    value.includes('instellingen')
    || value.includes('changelog')
    || value.includes('nieuwefeatures')
    || value.includes('kosten')
    || value.includes('appsamenvatting')
    || value.includes('appstatus')
    || value.includes('bestandenoverzicht')
    || value.includes('cookie_migration')
  ) {
    return 'Functioneel Beheer';
  }

  return 'Development docs';
}

export function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function toDayKey(value) {
  const dt = toDate(value);
  return dt ? dt.toISOString().slice(0, 10) : '';
}

export function parseTravelNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value == null) return 0;
  const numeric = Number(String(value).trim().replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : 0;
}

export function getTravelHoursFromAny(row = {}) {
  const hourCandidates = [
    row.reisTijd,
    row.reistijdHours,
    row.reistijd,
    row.travelHours,
    row.travel,
    row.hours,
    row.durationHours,
  ];
  for (const candidate of hourCandidates) {
    const parsed = parseTravelNumber(candidate);
    if (parsed > 0) return parsed;
  }

  const minuteCandidates = [
    row.reisTijdMinuten,
    row.reistijdMinuten,
    row.travelMinutes,
    row.minutes,
    row.minuten,
  ];
  for (const candidate of minuteCandidates) {
    const minutes = parseTravelNumber(candidate);
    if (minutes > 0) return minutes / 60;
  }

  return 0;
}

export function groupRobotsByGemeente(robots = []) {
  const grouped = new Map();

  (robots || []).forEach((robot) => {
    const gemeente = String(robot?.gemeente || 'Onbekende gemeente').trim() || 'Onbekende gemeente';
    if (!grouped.has(gemeente)) grouped.set(gemeente, []);
    grouped.get(gemeente).push(robot);
  });

  return Array.from(grouped.entries())
    .map(([gemeente, gemeenteRobots]) => ({
      gemeente,
      robots: [...gemeenteRobots].sort((a, b) => {
        const nameA = String(a?.name || a?.naam || a?.id || 'Naamloos');
        const nameB = String(b?.name || b?.naam || b?.id || 'Naamloos');
        return nameA.localeCompare(nameB, 'nl', { numeric: true, sensitivity: 'base' });
      })
    }))
    .sort((a, b) => a.gemeente.localeCompare(b.gemeente, 'nl', { sensitivity: 'base' }));
}

export function getRobotGemeenteOptions(robotsByGemeente = []) {
  return robotsByGemeente.map(({ gemeente }) => gemeente);
}

export function filterRobotGroups(robotsByGemeente = [], robotGemeenteFilter = '') {
  if (!robotGemeenteFilter) return robotsByGemeente;
  const normalizedFilter = String(robotGemeenteFilter).trim().toLowerCase();
  return robotsByGemeente.filter(({ gemeente }) => String(gemeente).trim().toLowerCase() === normalizedFilter);
}

export function deriveManagedUserRole(currentUser = {}, overrides = {}) {
  const merged = { ...currentUser, ...overrides };
  if (merged.isSuperAdmin === true) return 'SuperAdmin';
  if (merged.isGlobalAdmin === true) return 'GlobalAdmin';
  if (merged.admin === true) return 'Admin';
  if (merged.technician === true) return 'Technician';
  return 'Viewer';
}

export function sortSerialDesc(a, b) {
  const getNumericValue = (code) => {
    if (!code) return 0;

    const matches = String(code).match(/(\d+)/g);
    if (matches) {
      const numStr = matches.join('');
      const num = parseInt(numStr, 10);
      return Number.isNaN(num) ? 0 : num;
    }

    const fullNum = parseInt(String(code).replace(/[^0-9]/g, ''), 10);
    return Number.isNaN(fullNum) ? 0 : fullNum;
  };

  const numA = getNumericValue(a?.code);
  const numB = getNumericValue(b?.code);

  if (numA > 0 || numB > 0) {
    return numB - numA;
  }

  return String(a?.code || '').localeCompare(String(b?.code || ''));
}

export function normalizeMinimumStock(value, fallback = 1) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.floor(parsed));
}
